import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Select, Space, Spin, Typography } from 'antd';
import {
    ArrowLeftOutlined,
    EnvironmentOutlined,
    FileTextOutlined,
    LinkOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Report from '../components/Report';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import '../style/Report.css';

const { Text } = Typography;

interface MeasurementSummary {
    id: number;
    measurementCode?: string;
    createdAt?: string;
    measuredAt?: string | null;
    geom?: { type: string; coordinates: number[] } | null;
    area?: { id: number; name: string; areaType?: { id: number; name: string } | null } | null;
    pattern?: { id: number; name: string } | null;
}

interface MeasurementReportTemplate {
    id?: number | string;
    key: string;
    name: string;
    description?: string;
    sections: any[];
    [key: string]: any;
}

const DEFAULT_MEASUREMENT_TEMPLATE: MeasurementReportTemplate = {
    id: 'measurement-default',
    key: 'measurement-default',
    name: 'Measurement report',
    description: 'Complete measurement details',
    scope: 'measurement',
    sections: [
        { key: 'measurement-info', visual: 'measurement-info', title: 'Measurement information', span: 24 },
        { key: 'measurement-values', visual: 'measurement-values', title: 'Measurement values', span: 12 },
        {
            key: 'measurement-texts',
            visual: 'measurement-texts',
            title: 'Measurement notes',
            span: 12,
            interpretTextAsJson: true,
        },
        { key: 'measurement-files', visual: 'measurement-files', title: 'Files', span: 24 },
        { key: 'measurement-weather', visual: 'measurement-weather', title: 'Weather', span: 12 },
        { key: 'measurement-location', visual: 'measurement-location', title: 'Location', span: 12 },
        {
            key: 'measurement-pattern-location',
            visual: 'measurement-pattern-location',
            title: 'Pattern location',
            span: 24,
        },
        { key: 'latest-analysis', visual: 'latest-analysis', title: 'AI analysis', span: 24 },
    ],
};

const MeasurementReportPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const measurementId = Number(id);
    const [measurement, setMeasurement] = useState<MeasurementSummary | null>(null);
    const [templates, setTemplates] = useState<MeasurementReportTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [templateWarning, setTemplateWarning] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        if (!Number.isInteger(measurementId) || measurementId <= 0) {
            setError('Invalid measurement ID');
            setLoading(false);
            return () => {
                cancelled = true;
            };
        }

        const load = async () => {
            setLoading(true);
            setError(null);
            setTemplateWarning(null);

            try {
                const measurementResponse = await axios.get(
                    ENDPOINTS.MEASUREMENT_DETAIL(measurementId),
                    { withCredentials: true },
                );
                if (cancelled) return;

                const measurementData = measurementResponse.data?.data || measurementResponse.data;
                if (!measurementData?.id) {
                    throw new Error('Measurement not found');
                }
                setMeasurement(measurementData);

                try {
                    const areaTypeId = measurementData.area?.areaType?.id;
                    const templateResponse = await axios.get(ENDPOINTS.REPORT_TEMPLATES, {
                        params: {
                            measurementId,
                            status: 'active',
                            navigationContext: 'measurement',
                            ...(areaTypeId ? { areaTypeId } : {}),
                        },
                        withCredentials: true,
                    });
                    if (cancelled) return;
                    const templateData = templateResponse.data?.data || templateResponse.data || [];
                    setTemplates(Array.isArray(templateData) ? templateData : []);
                } catch (templateError) {
                    if (cancelled) return;
                    console.warn('[MeasurementReportPage] Failed to load report templates', templateError);
                    setTemplates([]);
                    setTemplateWarning('Configured templates could not be loaded. Showing the complete default view.');
                }
            } catch (loadError: any) {
                if (cancelled) return;
                console.error('[MeasurementReportPage] Failed to load measurement', loadError);
                setMeasurement(null);
                setError(loadError?.response?.status === 404 ? 'Measurement not found' : 'Failed to load measurement');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [measurementId]);

    const requestedTemplateId = searchParams.get('template');
    const selectedTemplate = useMemo(() => {
        if (templates.length === 0) return DEFAULT_MEASUREMENT_TEMPLATE;
        if (requestedTemplateId) {
            const requested = templates.find((template) => String(template.id) === requestedTemplateId);
            if (requested) return requested;
        }
        return templates[0];
    }, [requestedTemplateId, templates]);

    const changeTemplate = (templateId: number | string) => {
        const next = new URLSearchParams(searchParams);
        next.set('template', String(templateId));
        setSearchParams(next, { replace: true });
    };

    if (loading) {
        return (
            <div className="measurement-report-page measurement-report-page--state">
                <Spin size="large" tip="Loading measurement..." />
            </div>
        );
    }

    if (error || !measurement) {
        return (
            <div className="measurement-report-page">
                <Space direction="vertical" size="middle">
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/measurements')}>
                        Back to measurements
                    </Button>
                    <Alert message={error || 'Measurement not found'} type="error" showIcon />
                </Space>
            </div>
        );
    }

    const reportDate = measurement.measuredAt || measurement.createdAt;

    return (
        <div className="measurement-report-page">
            <div className="measurement-report-fixed-header">
                <div className="measurement-report-fixed-main">
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/measurements')}>
                        Back
                    </Button>
                    <div className="measurement-report-fixed-identity">
                        <FileTextOutlined className="measurement-report-fixed-icon" />
                        <Text type="secondary" className="measurement-report-fixed-label">Measurement</Text>
                        <span className="measurement-report-fixed-code">
                            {measurement.measurementCode || `Measurement ${measurement.id}`}
                        </span>
                    </div>
                    <Space wrap className="measurement-report-fixed-actions">
                        <Button
                            icon={<EnvironmentOutlined />}
                            onClick={() => navigate(`/measurements/${measurement.id}/edit-location`)}
                        >
                            {measurement.geom ? 'Edit' : 'Set'} GPS location
                        </Button>
                        {!measurement.area ? (
                            <Button
                                type="primary"
                                icon={<LinkOutlined />}
                                onClick={() => navigate(`/measurements/assign-areas?measurementId=${measurement.id}`)}
                            >
                                Assign to area
                            </Button>
                        ) : null}
                        {measurement.area && !measurement.pattern ? (
                            <Button
                                type="primary"
                                icon={<LinkOutlined />}
                                onClick={() => navigate(`/measurements/assign-patterns?measurementId=${measurement.id}`)}
                            >
                                Assign to pattern
                            </Button>
                        ) : null}
                    </Space>
                </div>

                {templates.length > 1 ? (
                    <div className="measurement-report-template-picker">
                        <Text strong>Report template</Text>
                        <Select
                            value={selectedTemplate.id}
                            onChange={changeTemplate}
                            options={templates.map((template) => ({
                                label: template.name,
                                value: template.id as number,
                            }))}
                            style={{ minWidth: 260 }}
                        />
                    </div>
                ) : null}
            </div>

            {templateWarning ? (
                <Alert className="measurement-report-template-alert" message={templateWarning} type="warning" showIcon />
            ) : null}

            <Report
                template={selectedTemplate}
                areaId={measurement.area?.id}
                measurementId={measurement.id}
                dateFrom={reportDate}
                dateTo={reportDate}
                showControls={false}
                lockScope
            />
        </div>
    );
};

export default MeasurementReportPage;
